import {
  collection, doc, getDoc, getDocs, limit, query, where,
} from "firebase/firestore";

import { db } from "../firebase";
import { hasCreatorAccess } from "../utils/roleUtils";
import { getDropStatus } from "../pages/creator/creatorHelpers";

export function isPaidArchive(afterClose) {
  return afterClose === "archive" || afterClose === "paid_archive";
}

export function isGoneForever(afterClose) {
  return !afterClose || afterClose === "forever" || afterClose === "gone_forever";
}

export async function fetchDropWithCreator(dropId) {
  if (!dropId) return { drop: null, creator: null };

  const dropSnap = await getDoc(doc(db, "drops", dropId));
  if (!dropSnap.exists()) return { drop: null, creator: null };

  const drop = { id: dropSnap.id, ...dropSnap.data() };
  let creator = null;

  if (drop.creatorId) {
    const creatorSnap = await getDoc(doc(db, "users", drop.creatorId));
    if (creatorSnap.exists()) {
      creator = { id: creatorSnap.id, ...creatorSnap.data() };
    }
  }

  return { drop, creator };
}

export async function fetchFollowedOpenDrops(userId, currentDropId) {
  if (!userId) return [];

  const followsSnap = await getDocs(
    query(collection(db, "follows"), where("followerId", "==", userId))
  );
  const creatorIds = new Set(
    followsSnap.docs.map((d) => d.data().creatorId).filter(Boolean)
  );
  if (!creatorIds.size) return [];

  const dropsSnap = await getDocs(
    query(collection(db, "drops"), where("status", "==", "open"), limit(100))
  );

  return dropsSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((d) => creatorIds.has(d.creatorId) && getDropStatus(d) === "open")
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    .map((d) => d.id)
    .filter((id) => id !== currentDropId);
}

export async function fetchFirstViewRecord(userId, dropId) {
  if (!userId || !dropId) return null;
  try {
    const snap = await getDoc(doc(db, "first_views", `${dropId}_${userId}`));
    if (!snap.exists()) return null;
    const data = snap.data();
    const rank = data.rank || data.viewNumber || null;
    return { ...data, rank };
  } catch {
    return null;
  }
}

export async function fetchFirstViewers(dropId, max = 5) {
  if (!dropId) return [];

  const snap = await getDocs(
    query(
      collection(db, "first_views"),
      where("dropId", "==", dropId),
      limit(100)
    )
  ).catch(() =>
    getDocs(
      query(
        collection(db, "first_views"),
        where("momentId", "==", dropId),
        limit(100)
      )
    )
  );

  const rows = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.rank || 999) - (b.rank || 999))
    .slice(0, max);

  const enriched = await Promise.all(
    rows.map(async (row) => {
      if (!row.userId) return { ...row, displayName: "Viewer", avatarUrl: "" };
      const userSnap = await getDoc(doc(db, "users", row.userId));
      const u = userSnap.exists() ? userSnap.data() : {};
      return {
        ...row,
        displayName: u.displayName || u.username || "Viewer",
        avatarUrl: u.avatarUrl || u.avatar || "",
        handle: u.handle || u.username || "",
        isCreator: hasCreatorAccess(u),
        pinnedTrophy: u.pinnedTrophy || null,
      };
    })
  );

  return enriched;
}

export async function fetchCreatorOtherDrops(creatorId, excludeDropId, max = 4) {
  if (!creatorId) return [];

  const snap = await getDocs(
    query(collection(db, "drops"), where("creatorId", "==", creatorId), limit(24))
  );

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((d) => d.id !== excludeDropId)
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    .slice(0, max);
}

/**
 * Creator catalog for Drop View swipe — includes the current drop, excludes drafts,
 * newest first. Same single-field creatorId query as fetchCreatorOtherDrops (no
 * composite index). Current drop is always kept even if it were drafted.
 */
export async function fetchCreatorDropCatalog(creatorId, currentDropId, max = 24) {
  if (!creatorId) return [];

  const snap = await getDocs(
    query(collection(db, "drops"), where("creatorId", "==", creatorId), limit(40))
  );

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((d) => {
      if (currentDropId && d.id === currentDropId) return true;
      return getDropStatus(d) !== "draft" && d.status !== "draft";
    })
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    .slice(0, max);
}

export async function fetchCreatorDropCount(creatorId) {
  if (!creatorId) return 0;
  const snap = await getDocs(
    query(collection(db, "drops"), where("creatorId", "==", creatorId))
  );
  return snap.size;
}

export async function fetchAlsoWatchedByFollowing(userId, dropId, maxAvatars = 3) {
  if (!userId || !dropId) return { count: 0, viewers: [] };

  // Following list = creators/users this viewer follows (follows collection)
  const followsSnap = await getDocs(
    query(collection(db, "follows"), where("followerId", "==", userId))
  );
  const myFollowing = new Set(
    followsSnap.docs.map((d) => d.data().creatorId || d.data().followingId).filter(Boolean)
  );
  if (!myFollowing.size) return { count: 0, viewers: [] };

  const viewsSnap = await getDocs(
    query(collection(db, "first_views"), where("dropId", "==", dropId), limit(100))
  ).catch(() =>
    getDocs(query(collection(db, "first_views"), where("momentId", "==", dropId), limit(100)))
  );

  const matched = viewsSnap.docs
    .map((d) => d.data().userId)
    .filter((id) => id && id !== userId && myFollowing.has(id));

  const unique = [...new Set(matched)];
  const viewers = await Promise.all(
    unique.slice(0, maxAvatars).map(async (uid) => {
      const snap = await getDoc(doc(db, "users", uid));
      if (!snap.exists()) return null;
      const u = snap.data();
      return {
        userId: uid,
        avatarUrl: u.avatarUrl || u.avatar || "",
        handle: u.handle || u.username || "",
        isCreator: hasCreatorAccess(u),
        pinnedTrophy: u.pinnedTrophy || null,
      };
    })
  );

  return { count: unique.length, viewers: viewers.filter(Boolean) };
}

export async function fetchAlsoWatchedAvatars(dropId, limitCount = 3) {
  const viewers = await fetchFirstViewers(dropId, limitCount);
  return {
    count: viewers.length,
    avatars: viewers.map((v) => v.avatarUrl).filter(Boolean),
  };
}
